import {
  CHANGE_SELF_PASSWORD_URL,
  GET_USER_PROFILE_URL,
  UPDATE_USER_URL,
  get_headers,
} from "@/utils/constants/constants.js";
import alert_mixin from "@/utils/mixins/alert_mixin";
import {
  validate_non_empty_string,
  validate_password,
  validate_phone_number,
} from "@/utils/validations/validate_inputs";
import axios from "axios";

export default {
  name: "UserProfileComponent",
  mixins: [alert_mixin],
  data: () => ({
      user_details: null,
      edited_details: {},
      is_loading: true,
      is_editing: false,
      is_saving: false,
      is_changing_password: false,
      is_password_mode: false,
      error_message: null,
      validation_errors: {},
      password_form: {
        current_password: "",
        new_password: "",
        confirm_password: "",
      },
      show_current_password: false,
      show_new_password: false,
      show_confirm_new_password: false,
      password_validation: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
      },
      password_validation_errors: [],
      password_form_errors: {},
      entity_profile_id: null,
      user_id: null,
      headers: get_headers(),
  }),
  props: {
    profile_id: {
      type: Number,
      required: false,
      default: null,
    },
    user_identifier: {
      type: Number,
      required: false,
      default: null,
    },
    allow_edit: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  mounted() {
    this.fetch_user_details();
  },
  methods: {
    async fetch_user_details() {
      this.is_loading = true;
      this.error_message = null;
      this.user_details = null;

      try {
        const params = {};

        if (this.profile_id) {
          params.entity_profile_id = this.profile_id;
        } else if (this.user_identifier) {
          params.user_id = this.user_identifier;
        }

        const response = await axios.get(GET_USER_PROFILE_URL, {
          headers: this.headers,
          params: params,
        });

        if (response.data.status === "success") {
          this.user_details = response.data.data;
          this.edited_details = this.normalize_editable_details(this.user_details);
        } else {
          this.error_message = response.data.message || "Unable to retrieve user details";
        }
      } catch (error) {
        const backend_message = error.response?.data?.message || "";

        if (backend_message === "No user associated with this entity profile") {
          this.error_message = "This application is not associated with a user yet.";
          return;
        }

        this.error_message =
          backend_message || error.message || "An error occurred while fetching user details";
        this.show_error(this.error_message);
      } finally {
        this.is_loading = false;
      }
    },
    start_editing() {
      if (!this.allow_edit) {
        return;
      }
      this.is_editing = true;
      this.validation_errors = {};
      this.edited_details = this.normalize_editable_details(this.user_details);
    },
    cancel_editing() {
      this.is_editing = false;
      this.validation_errors = {};
      this.edited_details = this.normalize_editable_details(this.user_details);
    },
    normalize_editable_details(details) {
      const normalized_details = {
        first_name: details?.first_name ?? "",
        last_name: details?.last_name ?? "",
        email: details?.email ?? "",
        phone_number: details?.phone_number ?? "",
      };
      return normalized_details;
    },
    update_password_validation(password) {
      const result = validate_password(password || "");
      this.password_validation = result.validation;
      this.password_validation_errors = result.errors;
    },
    open_password_mode() {
      this.is_password_mode = true;
      this.password_form_errors = {};
      this.update_password_validation("");
    },
    cancel_password_mode() {
      this.is_password_mode = false;
      this.reset_password_form();
    },
    toggle_new_password_visibility() {
      this.show_new_password = !this.show_new_password;
    },
    toggle_current_password_visibility() {
      this.show_current_password = !this.show_current_password;
    },
    toggle_confirm_new_password_visibility() {
      this.show_confirm_new_password = !this.show_confirm_new_password;
    },
    validate_form() {
      const errors = {};
      const first_name = (this.edited_details.first_name || "").trim();
      const last_name = (this.edited_details.last_name || "").trim();
      const email = (this.edited_details.email || "").trim();
      const phone_number = (this.edited_details.phone_number || "").trim();

      if (!validate_non_empty_string(first_name)) {
        errors.first_name = "First name is required.";
      }

      if (!validate_non_empty_string(last_name)) {
        errors.last_name = "Last name is required.";
      }

      if (!validate_non_empty_string(email)) {
        errors.email = "Email address is required.";
      }

      if (phone_number && !validate_phone_number(phone_number)) {
        errors.phone_number = "Phone number must contain 10 digits.";
      }

      this.validation_errors = errors;
      return Object.keys(errors).length === 0;
    },
    async save_changes() {
      if (!this.validate_form()) {
        this.show_error("Please fix the highlighted profile fields.");
        return;
      }

      this.is_saving = true;

      const payload = {
        user_id: this.user_details?.id || this.user_identifier || Number(sessionStorage.getItem("user_id")) || null,
        first_name: this.edited_details.first_name.trim(),
        last_name: this.edited_details.last_name.trim(),
        email: this.edited_details.email.trim(),
      };

      const normalized_phone_number = (this.edited_details.phone_number || "").trim();
      if (normalized_phone_number) {
        payload.phone_number = normalized_phone_number;
      }

      try {
        const response = await axios.post(UPDATE_USER_URL, payload, {
          headers: this.headers,
        });

        if (response.data.status === "success") {
          const updated = response.data.data || {};
          this.user_details = {
            ...this.user_details,
            ...updated,
            full_name: `${updated.first_name || payload.first_name} ${updated.last_name || payload.last_name}`.trim(),
          };
          this.edited_details = this.normalize_editable_details(this.user_details);
          this.validation_errors = {};
          this.is_editing = false;
          this.show_success("Profile details updated successfully.");
          return;
        }

        this.show_error(response.data.message || "Unable to update profile details.");
      } catch (error) {
        const api_errors = error.response?.data?.data;
        if (api_errors && typeof api_errors === "object") {
          const mapped_errors = {};
          Object.keys(api_errors).forEach((key) => {
            const value = Reflect.get(api_errors, key);
            Reflect.set(mapped_errors, key, Array.isArray(value) ? value[0] : String(value));
          });
          this.validation_errors = {
            ...this.validation_errors,
            ...mapped_errors,
          };
        }

        this.show_error(
          error.response?.data?.message ||
          "An error occurred while updating profile details."
        );
      } finally {
        this.is_saving = false;
      }
    },
    reset_password_form() {
      this.password_form = {
        current_password: "",
        new_password: "",
        confirm_password: "",
      };
      this.password_form_errors = {};
      this.password_validation_errors = [];
      this.password_validation = {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
      };
      this.show_current_password = false;
      this.show_new_password = false;
      this.show_confirm_new_password = false;
    },
    validate_password_form() {
      const errors = {};
      const current_password = this.password_form.current_password || "";
      const new_password = this.password_form.new_password || "";
      const confirm_password = this.password_form.confirm_password || "";

      this.update_password_validation(new_password);

      if (!validate_non_empty_string(current_password)) {
        errors.current_password = "Current password is required.";
      }

      if (!validate_non_empty_string(new_password)) {
        errors.new_password = "New password is required.";
      } else if (this.password_validation_errors.length > 0) {
        errors.new_password = this.password_validation_errors[0];
      }

      if (!validate_non_empty_string(confirm_password)) {
        errors.confirm_password = "Please confirm your new password.";
      } else if (new_password !== confirm_password) {
        errors.confirm_password = "Passwords do not match.";
      }

      this.password_form_errors = errors;
      return Object.keys(errors).length === 0;
    },
    async change_password() {
      if (!this.validate_password_form()) {
        this.show_error("Please fix the highlighted password fields.");
        return;
      }

      this.is_changing_password = true;

      try {
        const response = await axios.post(
          CHANGE_SELF_PASSWORD_URL,
          {
            current_password: this.password_form.current_password,
            new_password: this.password_form.new_password,
            confirm_password: this.password_form.confirm_password,
          },
          { headers: this.headers }
        );

        if (response.data.status === "success") {
          this.cancel_password_mode();
          this.show_success("Password changed successfully.");
          return;
        }

        this.show_error(response.data.message || "Unable to change password.");
      } catch (error) {
        const data = error.response?.data?.data;
        const errors = {};

        if (Array.isArray(data) && data.length > 0) {
          errors.new_password = data[0];
        } else if (data && typeof data === "object") {
          Object.keys(data).forEach((key) => {
            const value = Reflect.get(data, key);
            Reflect.set(errors, key, Array.isArray(value) ? value[0] : String(value));
          });
        }

        this.password_form_errors = {
          ...this.password_form_errors,
          ...errors,
        };

        this.show_error(
          error.response?.data?.message ||
          errors.current_password ||
          errors.new_password ||
          "An error occurred while changing the password."
        );
      } finally {
        this.is_changing_password = false;
      }
    },
    format_date(date_string) {
      if (!date_string) return "N/A";
      return new Date(date_string).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    get_status_class(is_active, is_blocked) {
      if (is_blocked) return "status_blocked";
      if (is_active) return "status_active";
      return "status_inactive";
    },
    get_verification_class(is_verified) {
      return is_verified ? "verified" : "unverified";
    },
    get_initials() {
      const first = this.user_details?.first_name?.charAt(0) || "";
      const last = this.user_details?.last_name?.charAt(0) || "";
      return (first + last).toUpperCase() || "U";
    },
    get_display_value(value, fallback = "Not provided") {
      return value && String(value).trim() ? value : fallback;
    },
    get_requirement_text(requirement) {
      const texts = Object.freeze({
        length: "At least 8 characters",
        uppercase: "One uppercase letter",
        lowercase: "One lowercase letter",
        number: "One number",
        special: "One special character",
      });

      const message = Reflect.get(texts, requirement) || "Invalid requirement";
      return message;
    },
  },
};
